import { moneyToNumber } from "@babybjorn/utils/commercetools/money";
import type {
	Cart,
	LineItem,
	Order,
	ProductVariant,
} from "@commercetools/platform-sdk";
import { sha256 } from "js-sha256";
import { getVariantAttributeValue } from "../../utils/commercetools/variant-service";
import type { ExpandedOrder } from "../commercetools/order-service";
import { pushEvent } from "./gtm";

const getLineItemDiscount = (lineItem: LineItem): number => {
	const lineItemPrice = moneyToNumber(lineItem.price.value);
	const discounted = Boolean(lineItem.price.discounted);
	const discountedPrice = moneyToNumber(lineItem.price.discounted?.value);

	return discounted ? lineItemPrice - discountedPrice : 0;
};

export interface EcommerceItem {
	name: string;
	id: string;
	price: string;
	brand: string;
	category?: string;
	variant: string;
	quantity?: number;
}

export const getEcommerceItem = (lineItem: LineItem): EcommerceItem => {
	return {
		name: lineItem.name[process.env.GATSBY_LANGUAGE || ""] || "",
		id: lineItem.variant.sku || "",
		price: moneyToNumber(lineItem.price.value).toString(),
		brand: "Babybjorn",
		category:
			getVariantAttributeValue(lineItem.variant, "gtm-category-name") ||
			undefined,
		variant: lineItem.variant.key || "",
		quantity: lineItem.quantity,
	};
};

const getActiveTransactionId = (order: Order): string => {
	let transactionId = "";
	for (const payment of order.paymentInfo?.payments || []) {
		for (const transaction of payment.obj?.transactions || []) {
			if (transaction.state === "Success" && transaction.type === "Charge") {
				transactionId = transaction.id;
			}
		}
	}

	return transactionId;
};

const getSkuForVariantAndStore = (
	variant: ProductVariant,
	storeKey: string,
) => {
	const skuValue = getVariantAttributeValue(
		variant,
		`${storeKey.toLowerCase()}-sku`,
	);

	return skuValue || variant.sku;
};

interface UseGtmVariablesProps {
	order?: ExpandedOrder;
	cart?: Cart | null;
}

/**
 * This function makes sure to sync variables in the GTM so they are up to date.
 */
export const updateGtmVariables = ({ order, cart }: UseGtmVariablesProps) => {
	const orderOrCart = order ?? cart;

	const orderOrCartCodes =
		orderOrCart?.discountCodes?.map((code) => code.discountCode.obj?.code) ||
		[];

	const orderCodes =
		order?.discountCodes?.map((code) => code.discountCode.obj?.code) || [];

	let subtotal = 0;
	if (orderOrCart?.lineItems) {
		for (const lineItem of orderOrCart.lineItems) {
			subtotal +=
				moneyToNumber(
					lineItem.price.discounted?.value ??
						orderOrCart.taxedPrice?.totalNet ??
						lineItem.totalPrice,
				) * lineItem.quantity;
		}
	}

	const orderTotal = moneyToNumber(
		orderOrCart?.taxedPrice?.totalGross ?? orderOrCart?.totalPrice,
	);

	const orderTotalGross = moneyToNumber(order?.taxedPrice?.totalGross);

	const orderTotalNet = moneyToNumber(order?.taxedPrice?.totalNet);

	const storeKey = orderOrCart?.store?.key;
	const lineItems = orderOrCart?.lineItems;
	const email = orderOrCart?.billingAddress?.email?.trim();

	const variablesData = {
		orderSubtotal: subtotal,
		orderTotal: orderTotal,
		order_id: order?.id,
		ecommerce: {
			// only uses order
			items: order?.lineItems.map(getEcommerceItem),
			currency: order?.totalPrice.currencyCode,
			transaction_id: order && getActiveTransactionId(order),
			value: moneyToNumber(order?.totalPrice),
			coupon: orderCodes[0],
			shipping: moneyToNumber(order?.shippingInfo?.price),
			tax: orderTotalGross - orderTotalNet,
			affiliation: `Babybjorn ${process.env.GATSBY_STORE}`,
		},
		cjAffilate: {
			// uses order if available, otherwise cart
			currency: orderOrCart?.totalPrice.currencyCode,
			subtotal: subtotal,
			coupon: orderOrCartCodes[0], // Just takes the first code currently. This might need to be changed.
			orderNumber: order?.orderNumber,
			eHash: email ? sha256(email) : "",
			products: lineItems?.map((lineItem) => {
				return {
					id: storeKey && getSkuForVariantAndStore(lineItem.variant, storeKey),
					unit_price: moneyToNumber(
						lineItem.price.discounted?.value ?? lineItem.price.value,
					),
					quantity: lineItem.quantity,
					discount: getLineItemDiscount(lineItem),
				};
			}),
		},
	};

	pushEvent(variablesData);
};
